import React, { useState, useEffect, useCallback } from 'react';
import { useLoading } from './LoadingOverlay/LoadingContext';
import { waitForNextPaint } from './LoadingOverlay/UtilsLoadingOverlay';

const ManagementPage = () =>{  // 共通消耗品編集ページ用コンポーネント
  // =========
  // 基本設定
  // =========
  const { showLoading, hideLoading } = useLoading();  // LoadingOverlayをセット
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);  // DBからデータリストを取得
  const [isFetchingList, setIsFetchingList] = useState(false);

  // 表の項目
  const [newLocationCodes, setNewLocationCodes] = useState(['']);
  const [newCategories, setNewCategories] = useState(['']);
  const [newProductNames, setNewProductNames] = useState(['']);
  const [newProductNumbers, setNewProductNumbers] = useState(['']);
  const [newManufacturers, setNewManufacturers] = useState(['']);
  const [newVendors, setNewVendors] = useState(['']);
  const [newSecondaryVendors, setNewSecondaryVendors] = useState(['']);
  const [newUnitPrices, setNewUnitPrices] = useState(['']);
  const [newUnits, setNewUnits] = useState(['']);
  const [newOrderQuantities, setNewOrderQuantities] = useState(['']);
  const [newDueDates, setNewDueDates] = useState(['']);
  const [newReorderPoints, setNewReorderPoints] = useState(['']);
  const [newPersonInCharges, setNewPersonInCharges] = useState(['']);
  const [newNotes, setNewNotes] = useState(['']);

  const [filterLocationCode, setFilterLocationCode] = useState('すべて');
  const [filterCategory, setFilterCategory] = useState('すべて');
  const [filterPersonInCharge, setFilterPersonInCharge] = useState('すべて');
  const [showInputs, setShowInputs] = useState([false]);  // 入力欄表示用state
  const [editBuffers, setEditBuffers] = useState({});
  const [sortOrder, setSortOrder] = useState('asc');  // 品名ソート用
  const [sortByProductName, setSortByProductName] = useState(false);  // 品名でソート中か
  
  // 保管場所の値を抽出（dataから動的に取得）
  const locationcodes = Array.from(new Set(data.map(row => row.LocationCode).filter(Boolean)));
  // 分類の値を抽出（dataから動的に取得）
  const categories = Array.from(new Set(data.map(row => row.Category).filter(Boolean)));
  // 担当者の値を抽出（dataから動的に取得）
  const personincharges = Array.from(new Set(data.map(row => row.PersonInCharge).filter(Boolean)));

  // フィルタ適用
  let filtered = data;
  if (filterLocationCode !== 'すべて') {
    filtered = filtered.filter(row => row.LocationCode === filterLocationCode);
  }
  if (filterCategory !== 'すべて') {
    filtered = filtered.filter(row => row.Category === filterCategory);
  }
  if (filterPersonInCharge !== 'すべて') {
    filtered = filtered.filter(row => row.PersonInCharge === filterPersonInCharge);
  }
  
  // フィルタ切り替え
  const handleFilterLocationCode = e => setFilterLocationCode(e.target.value);
  const handleFilterCategory = e => setFilterCategory(e.target.value);
  const handleFilterPersonInCharge = e => setFilterPersonInCharge(e.target.value);

  const keyname = 'ManagementPage_dataList';  // セッションストレージのキー名（ページ専用）
   //固定のAPIエンドポイントを指定
  const url = `https://kd4c0h9pca.execute-api.ap-northeast-1.amazonaws.com/Dev/LambdaToDynamoDB_DataEdit`;
  //固定URLにクエリパラメータを追加（Lambda関数でpage_nameを使って分岐させるため）
  const urlWithParams = new URL(url);
  urlWithParams.searchParams.set('page_name', 'ManagementPage');
  
  // ========
  // API設定
  // ========
  // タイムアウト付きHTTPリクエスト関数
  const fetchWithTimeout = async (urlWithParams, options = {}, timeout = 5000) => {
    const controller = new AbortController();  // タイムアウト用コントローラーを作成
    const signal = controller.signal;  // fetchに渡すsignalを取得
    const fetchPromise = fetch(urlWithParams, { ...options, signal });  // fetchをsignal付きで渡す
    const timeoutId = setTimeout(() => {
      controller.abort();  // タイムアウトが来たらfetchを中断
    }, timeout);
    try {
      return await fetchPromise;  // fetchの結果を待つ
    } finally {
      clearTimeout(timeoutId);  // fetchが完了したらタイマーを解除
    }
  };

  // 再トライ付きAPI呼び出し関数（GET/POST両対応）
  const fetchAPI = async (urlWithParams, options = {}, retries = 2, timeout = 5000) => {
    let attempts = 0;
    let lastError = null;
    while (attempts < retries) {
      try {
        const response = await fetchWithTimeout(urlWithParams, options, timeout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();  // 成功時にJSONデータを返す
      } catch (error) {
        lastError = error;
        attempts += 1;
        console.warn(`Attempt ${attempts} failed: ${error.message}`);
        if (attempts >= retries) {
            throw lastError;
        }
      }
    }
  };

  // 将来使用予定
  // ログインしていないユーザーをログインページへリダイレクトする
  // useEffect(() => {
  //   if (!auth) {
  //       navigate('/login');
  //   }
  // }, [auth, navigate]);

  // ===========
  // データ取得
  // ===========
  const GetList = useCallback(async ({ withLoading = true } ={}) => {
    setIsFetchingList(true);  // テーブルを隠す（非表示）
    if (withLoading) showLoading('データ読み込み中...');  // ユーザーに見せるメッセージ
    setError(null);
 
    // APIメソッドとデータ形式を特定
    const options = {
        method: 'GET',  // GETまたはPOST
        headers: {
            'Content-Type': 'application/json',  // 送受信するデータをJSON形式とするを示す
        },
    };

    try {
      const result = await fetchAPI(urlWithParams,options);
      console.log("GetList:::",result);
      
      // レスポンス形式を柔軟に処理
      let items = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result.Items && Array.isArray(result.Items)) {
        items = result.Items;
      } else if (result.body) {
        // Lambda関数がbodyプロパティでデータを返す場合
        const bodyData = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
        items = Array.isArray(bodyData) ? bodyData : (bodyData.Items || []);
      } else {
        console.warn('予期しないレスポンス形式:', result);
        items = [];
      }
      
      // データの形式を統一（Category～Notes）
      const formattedData = items.map(item => ({
        LocationCode: item.LocationCode || item.locationcode || '',
        Category: item.Category || item.category || '',
        ProductName: item.ProductName || item.productname || '',
        ProductNumber: item.ProductNumber || item.productnumber || '',
        Manufacturer: item.Manufacturer || item.manufacturer || '',
        Vendor: item.Vendor || item.vendor || '',
        SecondaryVendor: item.SecondaryVendor || item.secondaryvendor || '',
        UnitPrice: item.UnitPrice || item.unitprice || '',
        Unit: item.Unit || item.unit || '',
        OrderQuantity: item.OrderQuantity || item.orderquantity || '',
        DueDate: item.DueDate || item.duedate || '',
        ReorderPoint: item.ReorderPoint || item.reorderpoint || '',
        PersonInCharge: item.PersonInCharge || item.personincharge || '',
        Notes: item.Notes || item.notes || ''
      }));
      
      console.log('フォーマット済みデータ:', formattedData);
      setData(formattedData);  // 統一されたstateに保存
      setEditBuffers({});
    
      // データをブラウザのセッションストレージに保存
      // ページをリロードしても、同じセッション中ならデータを保持できる
      console.log('keyname:::',keyname);
      sessionStorage.setItem(keyname, JSON.stringify(formattedData));
    } catch (error) {
        console.error('API呼び出しエラー:', error);
        const errorMessage = error.message || 'データの取得に失敗しました。しばらくしてから再度お試しください。';
        setError(errorMessage);
        setData([]);  // エラー時は空の配列を設定
    } finally {
      setIsFetchingList(false);  // テーブルを戻す（表示）
      hideLoading();
    }
  }, [showLoading, hideLoading, keyname]);

  // ===========
  // データ登録
  // ===========
  const canRegisterNewRow = (index) => {
    const locationcode = (newLocationCodes[index] || '').trim();
    const category = (newCategories[index] || '').trim();
    const productname = (newProductNames[index] || '').trim();
    const productnumber = (newProductNumbers[index] || '').trim();
    const manufacturer = (newManufacturers[index] || '').trim();
    const vendor = (newVendors[index] || '').trim();
    const unitprice = (newUnitPrices[index] || '').trim();
    const unit = (newUnits[index] || '').trim();
    const orderquantity = (newOrderQuantities[index] || '').trim();
    const duedate = (newDueDates[index] || '').trim();
    const personincharge = (newPersonInCharges[index] || '').trim();
    return !!showInputs[index] && !!category && !!productname && !!productnumber && !!manufacturer && !!vendor && !!unitprice && !!unit && !!orderquantity && !!duedate && !!locationcode && !!personincharge;
  };

  const cancelNewInput = (index) => {
    const newShowInputs = [...showInputs];
    newShowInputs[index] = false;
    setShowInputs(newShowInputs);

    const resetField = (setter) => {
      setter(prev => {
        const copy = [...prev];
        copy[index] = '';
        return copy;
      });
    };

    resetField(setNewLocationCodes);
    resetField(setNewCategories);
    resetField(setNewProductNames);
    resetField(setNewProductNumbers);
    resetField(setNewManufacturers);
    resetField(setNewVendors);
    resetField(setNewSecondaryVendors);
    resetField(setNewUnitPrices);
    resetField(setNewUnits);
    resetField(setNewOrderQuantities);
    resetField(setNewDueDates);
    resetField(setNewReorderPoints);
    resetField(setNewPersonInCharges);
    resetField(setNewNotes);
  };

  // 登録ボタン押下時: 入力値をPOSTでDynamoDBへ登録
  const RegisterRole = async (index) => {
    if (!canRegisterNewRow(index)) {
      return;
    }

    const payload = {
      LocationCode: (newLocationCodes[index] || '').trim(),
      Category: (newCategories[index] || '').trim(),
      ProductName: (newProductNames[index] || '').trim(),
      ProductNumber: (newProductNumbers[index] || '').trim(),
      Manufacturer: (newManufacturers[index] || '').trim(),
      Vendor: (newVendors[index] || '').trim(),
      SecondaryVendor: (newSecondaryVendors[index] || '').trim(),
      UnitPrice: (newUnitPrices[index] || '').trim(),
      Unit: (newUnits[index] || '').trim(),
      OrderQuantity: (newOrderQuantities[index] || '').trim(),
      DueDate: (newDueDates[index] || '').trim(),
      ReorderPoint: (newReorderPoints[index] || '').trim(),
      PersonInCharge: (newPersonInCharges[index] || '').trim(),
      Notes: (newNotes[index] || '').trim()
    };

    // 簡易バリデーション
    if ( !payload.LocationCode || !payload.Category || !payload.ProductName || !payload.ProductNumber || !payload.Manufacturer || !payload.Vendor || !payload.UnitPrice || !payload.Unit || !payload.OrderQuantity || !payload.DueDate || !payload.PersonInCharge) {
      setError('必須項目（保管場所・分類・品名・品番・メーカー・購入先・単価（円）・単位・注文数量・納期（目安）・担当者）を入力してください。');
      return;
    }

    showLoading('登録中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };

    let success = false;

    try {
      // fetchAPIは非200で例外を投げるため、409を捕捉してメッセージ表示
      await fetchAPI(urlWithParams, options);

      // 成功時: 初期化して一覧再取得
      const newShowInputs = [...showInputs];
      newShowInputs[index] = false;
      setShowInputs(newShowInputs);
      setNewLocationCodes(['']);
      setNewCategories(['']);
      setNewProductNames(['']);
      setNewProductNumbers(['']);
      setNewManufacturers(['']);
      setNewVendors(['']);
      setNewSecondaryVendors(['']);
      setNewUnitPrices(['']);
      setNewUnits(['']);
      setNewOrderQuantities(['']);
      setNewDueDates(['']);
      setNewReorderPoints(['']);
      setNewPersonInCharges(['']);
      setNewNotes(['']);
      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('status: 409')) {
        setError('すでに登録済みです');
      } else {
        console.error('RegisterRole error:', err);
        setError('登録に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('登録しました。');
    }
  };

  // ===========
  // データ削除
  // ===========
  const DeleteRole = async (row) => {
    if (!row || !row.ProductName) {
      setError('削除対象の品名が不明です。');
      return;
    }

    const confirmed = window.confirm('本当に削除しますか');
    if (!confirmed) {
      return;
    }

    showLoading('削除中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ProductName: row.ProductName })
    };

    let success = false;

    try {
      // page_name を保持したまま action=delete を付与した URL を生成
      const deleteUrl = new URL(urlWithParams);
      deleteUrl.searchParams.set('action', 'delete');
      await fetchAPI(deleteUrl, options);

      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      console.error('DeleteRole error:', err);
      setError('削除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      hideLoading();
    }

    if (success) {
          // Overlayの非表示反映を待ってから次の処理へ進む
          await waitForNextPaint();
          alert('削除しました。');
        }
  };

  // ===========
  // データ更新
  // ===========
  const startEditRow = (row) => {
    if (!row || !row.ProductName || editBuffers[row.ProductName]) {
      return;
    }
    const normalized = {
      LocationCode: row.LocationCode || '',
      Category: row.Category || '',
      ProductNumber: row.ProductNumber || '',
      Manufacturer: row.Manufacturer || '',
      Vendor: row.Vendor || '',
      SecondaryVendor: row.SecondaryVendor || '',
      UnitPrice: row.UnitPrice || '',
      Unit: row.Unit || '',
      OrderQuantity: row.OrderQuantity || '',
      DueDate: row.DueDate || '',
      ReorderPoint: row.ReorderPoint || '',
      PersonInCharge: row.PersonInCharge || '',
      Notes: row.Notes || ''
    };
    setEditBuffers(prev => ({
      ...prev,
      [row.ProductName]: normalized
    }));
  };

  const handleEditFieldChange = (productname, field, value) => {
    if (!productname) return;
    setEditBuffers(prev => ({
      ...prev,
      [productname]: {
        ...(prev[productname] || { LocationCode: '', Category: '', ProductNumber: '', Manufacturer: '', Vendor: '', SecondaryVendor: '', UnitPrice: '', Unit: '', OrderQuantity: '', DueDate: '', ReorderPoint: '', PersonInCharge: '', Notes: '' }),
        [field]: value
      }
    }));
  };

  const hasRowChanges = (row) => {
    if (!row || !row.ProductName || !editBuffers[row.ProductName]) {
      return false;
    }
    const buffer = editBuffers[row.ProductName];
    return ['LocationCode', 'Category', 'ProductNumber', 'Manufacturer', 'Vendor', 'SecondaryVendor', 'UnitPrice', 'Unit', 'OrderQuantity', 'DueDate', 'ReorderPoint', 'PersonInCharge', 'Notes'].some(key => {
      const currentValue = buffer[key] ?? '';
      const originalValue = (row[key] ?? '');
      return currentValue !== originalValue;
    });
  };

  const clearEditStateForRow = (productname) => {
    setEditBuffers(prev => {
      const updated = { ...prev };
      delete updated[productname];
      return updated;
    });
  };

  const UpdateRole = async (row) => {
    if (!row || !row.ProductName || !editBuffers[row.ProductName] || !hasRowChanges(row)) {
      return;
    }

    const buffer = editBuffers[row.ProductName];
    const payload = {
      ProductName: row.ProductName,
      LocationCode: (buffer.LocationCode || '').trim(),
      Category: (buffer.Category || '').trim(),
      ProductNumber: (buffer.ProductNumber || '').trim(),
      Manufacturer: (buffer.Manufacturer || '').trim(),
      Vendor: (buffer.Vendor || '').trim(),
      SecondaryVendor: (buffer.SecondaryVendor || '').trim(),
      UnitPrice: (buffer.UnitPrice || '').trim(),
      Unit: (buffer.Unit || '').trim(),
      OrderQuantity: (buffer.OrderQuantity || '').trim(),
      DueDate: (buffer.DueDate || '').trim(),
      ReorderPoint: (buffer.ReorderPoint || '').trim(),
      PersonInCharge: (buffer.PersonInCharge || '').trim(),
      Notes: (buffer.Notes || '').trim()
    };

    if (!payload.LocationCode || !payload.Category || !payload.ProductNumber || !payload.Manufacturer || !payload.Vendor || !payload.UnitPrice || !payload.Unit || !payload.OrderQuantity || !payload.DueDate || !payload.PersonInCharge) {
      setError('必須項目（保管場所・分類・品番・メーカー・購入先・単価（円）・単位・注文数量・納期（目安）・担当者）を入力してください。');
      return;
    }

    showLoading('更新中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };

    let success = false;

    try {
      // page_name を保持したまま action=update を付与した URL を生成
      const updateUrl = new URL(urlWithParams);
      updateUrl.searchParams.set('action', 'update');
      await fetchAPI(updateUrl, options);

      clearEditStateForRow(row.ProductName);
      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      console.error('UpdateRole error:', err);
      setError('更新に失敗しました。時間をおいて再度お試しください。');
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('更新しました。');
    }
  };

  // ====================
  // 初回マウント時の処置
  // ====================
  // 初回マウント時にセッションストレージからデータを取得し、画面表示。 データがなければAPIを呼び出す
  useEffect(() => {
    const datalist = sessionStorage.getItem(keyname);
    // Datalistが存在し、内容に"null"という文字がないこと
    if (datalist && datalist != 'null') {  // セッションストレージ内にNullが生成されるため。その状態でlistに遷移するとmapできずエラーとなるため回避
      // Reactステートに取得データをセット（統一されたstateに保存）
      setData(JSON.parse(datalist));
        console.log("リスト取得完了");
    } else {
        // 有効なデータがない場合はAPIから取得
        GetList();
    }
  }, []);

  console.log("data:::",data);

  // =========
  // 表の機能
  // =========
  // 【共通消耗品登録表】
  // 品名入力処理
  const handleProductNameChange = (index, value) => {
    const newProductNamesCopy = [...newProductNames];
    newProductNamesCopy[index] = value;
    setNewProductNames(newProductNamesCopy);
  };
  // 保管場所入力処理
  const handleLocationCodeChange = (index, value) => {
    const newLocationCodesCopy = [...newLocationCodes];
    newLocationCodesCopy[index] = value;
    setNewLocationCodes(newLocationCodesCopy);
  };
  // 分類選択処理
  const handleCategoryChange = (index, value) => {
    const newCategoriesCopy = [...newCategories];
    newCategoriesCopy[index] = value;
    setNewCategories(newCategoriesCopy);
  };
  // 品番入力処理
  const handleProductNumberChange = (index, value) => {
    const newProductNumbersCopy = [...newProductNumbers];
    newProductNumbersCopy[index] = value;
    setNewProductNumbers(newProductNumbersCopy);
  };
  // メーカー入力処理
  const handleManufacturerChange = (index, value) => {
    const newManufacturersCopy = [...newManufacturers];
    newManufacturersCopy[index] = value;
    setNewManufacturers(newManufacturersCopy);
  };
  // 購入先入力処理
  const handleVendorChange = (index, value) => {
    const newVendorsCopy = [...newVendors];
    newVendorsCopy[index] = value;
    setNewVendors(newVendorsCopy);
  };
  // 二次購入先入力処理
  const handleSecondaryVendorChange = (index, value) => {
    const newSecondaryVendorsCopy = [...newSecondaryVendors];
    newSecondaryVendorsCopy[index] = value;
    setNewSecondaryVendors(newSecondaryVendorsCopy);
  };
  // 単価（円）入力処理
  const handleUnitPriceChange = (index, value) => {
    const newUnitPricesCopy = [...newUnitPrices];
    // 数値以外の文字を除去
    const numericValue = value.replace(/[^\d]/g, '');
    newUnitPricesCopy[index] = numericValue;
    setNewUnitPrices(newUnitPricesCopy);
  };
  // 単位選択処理
  const handleUnitChange = (index, value) => {
    const newUnitsCopy = [...newUnits];
    newUnitsCopy[index] = value;
    setNewUnits(newUnitsCopy);
  };
  // 注文数量入力処理
  const handleOrderQuantityChange = (index, value) => {
    const newOrderQuantitiesCopy = [...newOrderQuantities];
    newOrderQuantitiesCopy[index] = value;
    setNewOrderQuantities(newOrderQuantitiesCopy);
  };
  // 納期（目安）入力処理
  const handleDueDateChange = (index, value) => {
    const newDueDatesCopy = [...newDueDates];
    newDueDatesCopy[index] = value;
    setNewDueDates(newDueDatesCopy);
  };
  // 発注閾値（目安）入力処理
  const handleReorderPointChange = (index, value) => {
    const newReorderPointsCopy = [...newReorderPoints];
    newReorderPointsCopy[index] = value;
    setNewReorderPoints(newReorderPointsCopy);
  };
  // 担当者入力処理
  const handlePersonInChargeChange = (index, value) => {
    const newPersonInChargesCopy = [...newPersonInCharges];
    newPersonInChargesCopy[index] = value;
    setNewPersonInCharges(newPersonInChargesCopy);
  };
  // 備考入力処理
  const handleNotesChange = (index, value) => {
    const newNotesCopy = [...newNotes];
    newNotesCopy[index] = value;
    setNewNotes(newNotesCopy);
  };

  // ドロップダウン用の分類オプション
  const categoryOptions = ['ウエス類', '手袋類', '袋類', '化学物質・油脂・劇物・毒物・乾燥剤類', 'ブレーキ・エア配管類', '事務用品類', '容器類', 'ボルト・ナット・ワッシャ類', 'ホース・チューブ類', '熱電対類', '電線類', 'コネクタ類', '端子類', '安全備品類', '工具・測定具類', 'ヒューズ類', 'アルミシャーシ類', 'リレー類', '電子部品類', 'はんだ類', 'その他'];
  //const categoryOptions = categorieOptions; // エイリアス
  // ドロップダウン用の単位オプション
  const unitOptions = ['個', '式', '本', '巻', 'kg', 'm', '毎', '箱', '袋', '組/セット', '台', '束', '玉', '双/足', '缶', 'ml'];

  // 【登録済み共通消耗品表】
  // ソート処理（フィルタ適用後）
  const sortedData = (sortByProductName
    ? [...filtered].sort((a, b) => {
        if (a.ProductName < b.ProductName) return sortOrder === 'asc' ? -1 : 1;
        if (a.ProductName > b.ProductName) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      })
    : filtered);

  // 品名ヘッダークリック時
  const handleProductNameSort = () => {
    if (!sortByProductName) {
      setSortByProductName(true);
      setSortOrder('asc');
    } else {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    }
  };

  return (
    <div className='wrapper'>
      <h2 className='page-title'>共通消耗品編集</h2>
      
      {/* エラー表示 */}
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          fontSize: '0.6em', 
          color: '#ff4444',
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff4444',
          borderRadius: '4px',
          margin: '10px 0'
        }}>
          {error}
          <button 
            onClick={GetList}
            style={{
              marginLeft: '10px',
              padding: '4px 8px',
              fontSize: '0.5em',
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            再試行
          </button>
        </div>
      )}

      {/* ローディング中やエラー時はテーブルを非表示 */}
      {!isFetchingList && !error && (
        <div className='content-area'>
          <h3 className='table-title' style={{ margin: '10px 0 0 0.3em' }}>共通消耗品登録　　　※入力必須項目</h3>
          <div className='table-area table-area-spaced'>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>保管場所 ※</th>
                  <th>分類 ※</th>
                  <th>品名 ※</th>
                  <th>品番 ※</th>
                  <th>メーカー ※</th>
                  <th>購入先 ※</th>
                  <th>二次購入先</th>
                  <th>単価（円） ※</th>
                  <th>単位 ※</th>
                  <th>注文数量 ※</th>
                  <th>納期（目安） ※</th>
                  <th>発注閾値</th>
                  <th>担当者 ※</th>
                  <th>備考</th>
                  <th>編集</th>
                </tr>
              </thead>
              <tbody>
                {[0].map((idx) => (
                  <tr key={idx}>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newLocationCodes[idx] || ''}
                          onChange={(e) => handleLocationCodeChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <select
                          type="text"
                          value={newCategories[idx] || ''}
                          onChange={(e) => handleCategoryChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        >
                          <option value="">選択してください</option>
                          {categoryOptions.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newProductNames[idx] || ''}
                          onChange={(e) => handleProductNameChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newProductNumbers[idx] || ''}
                          onChange={(e) => handleProductNumberChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newManufacturers[idx] || ''}
                          onChange={(e) => handleManufacturerChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newVendors[idx] || ''}
                          onChange={(e) => handleVendorChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newSecondaryVendors[idx] || ''}
                          onChange={(e) => handleSecondaryVendorChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newUnitPrices[idx] || ''}
                          onChange={(e) => handleUnitPriceChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <select
                          value={newUnits[idx] || ''}
                          onChange={(e) => handleUnitChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        >
                          <option value="">選択してください</option>
                          {unitOptions.map(unit => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newOrderQuantities[idx] || ''}
                          onChange={(e) => handleOrderQuantityChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newDueDates[idx] || ''}
                          onChange={(e) => handleDueDateChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newReorderPoints[idx] || ''}
                          onChange={(e) => handleReorderPointChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newPersonInCharges[idx] || ''}
                          onChange={(e) => handlePersonInChargeChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newNotes[idx] || ''}
                          onChange={(e) => handleNotesChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      <div className='button-group'>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button 
                            style={{ 
                              backgroundColor: '#44aa44', 
                              opacity: showInputs[idx] ? 0.6 : 1,
                              cursor: showInputs[idx] ? 'not-allowed' : 'pointer'
                            }}
                            disabled={showInputs[idx]}
                            onClick={() => {
                              const newShowInputs = [...showInputs];
                              newShowInputs[idx] = true;
                              setShowInputs(newShowInputs);
                            }}
                          >
                            {showInputs[idx] ? '入力中' : '入力'}
                          </button>
                          {showInputs[idx] && (
                            <button 
                              style={{ backgroundColor: '#888888', }}
                              onClick={() => cancelNewInput(idx)}
                            >
                              キャンセル
                            </button>
                          )}
                          <button 
                            style={{ 
                              backgroundColor: canRegisterNewRow(idx) ? '#4444ff' : '#9aa0ff', 
                              cursor: canRegisterNewRow(idx) ? 'pointer' : 'not-allowed',
                              opacity: canRegisterNewRow(idx) ? 1 : 0.6
                            }}
                            disabled={!canRegisterNewRow(idx)}
                            onClick={() => RegisterRole(idx)}
                          >
                            登録
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      
          <h3 className='table-title'>登録済み共通消耗品　　　※入力必須項目</h3>
          <div className='table-area'>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>保管場所 ※</span>
                      <select value={filterLocationCode} onChange={handleFilterLocationCode} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                        {locationcodes.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>分類 ※</span>
                      <select value={filterCategory} onChange={handleFilterCategory} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={handleProductNameSort}
                  >
                    品名
                    <span style={{ color: sortOrder === 'asc' && sortByProductName ? '#222' : '#888', marginLeft: '2px' }}>▲</span>
                    <span style={{ color: sortOrder === 'desc' && sortByProductName ? '#222' : '#888' }}>▼</span>
                  </th>
                  <th>品番 ※</th>
                  <th>メーカー ※</th>
                  <th>購入先 ※</th>
                  <th>二次購入先</th>
                  <th>単価（円） ※</th>
                  <th>単位 ※</th>
                  <th>注文数量 ※</th>
                  <th>納期（目安） ※</th>
                  <th>発注閾値</th>
                  <th style={{ minWidth: '90px' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                      <span>担当者 ※</span>
                      <select value={filterPersonInCharge} onChange={handleFilterPersonInCharge} style={{ fontSize: '1em', marginLeft: '0.4em', width: '80px' }}>
                        <option value="すべて">すべて</option>
                        {personincharges.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th>備考</th>
                  <th>編集</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => {
                  const isEditing = !!editBuffers[row.ProductName];
                  const buffer = editBuffers[row.ProductName] || {};
                  const canUpdate = isEditing && hasRowChanges(row);
                  return (
                    <tr key={idx}>
                      <td>
                        {isEditing ? (
                          <input
                            value={buffer.LocationCode ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'LocationCode', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.LocationCode}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={buffer.Category ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Category', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          >
                            <option value="">選択してください</option>
                            {categoryOptions.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        ) : row.Category}
                      </td>
                      <td>{row.ProductName}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.ProductNumber ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'ProductNumber', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.ProductNumber}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Manufacturer ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Manufacturer', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Manufacturer}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Vendor ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Vendor', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Vendor}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.SecondaryVendor ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'SecondaryVendor', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.SecondaryVendor}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.UnitPrice ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'UnitPrice', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.UnitPrice}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={buffer.Unit ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Unit', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          >
                            <option value="">選択してください</option>
                              {unitOptions.map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                              ))}
                          </select>
                        ) : row.Unit}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.OrderQuantity ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'OrderQuantity', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.OrderQuantity}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            value={buffer.DueDate ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'DueDate', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.DueDate}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            value={buffer.ReorderPoint ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'ReorderPoint', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.ReorderPoint}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.PersonInCharge ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'PersonInCharge', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.PersonInCharge}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Notes ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ProductName, 'Notes', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Notes}
                      </td>
                      <td>
                        <div className='button-group'>
                          <button 
                            style={{ backgroundColor: '#44aa44' }}
                            disabled={isEditing}
                            onClick={() => startEditRow(row)}
                          >
                            {isEditing ? '編集中' : '編集'}
                          </button>
                          {isEditing && (
                            <button 
                              style={{ backgroundColor: '#888888' }}
                              onClick={() => clearEditStateForRow(row.ProductName)}
                            >
                              キャンセル
                            </button>
                          )}
                          <button 
                            style={{ 
                              backgroundColor: canUpdate ? '#4444ff' : '#9aa0ff',
                              cursor: canUpdate ? 'pointer' : 'not-allowed',
                              opacity: canUpdate ? 1 : 0.6
                            }}
                            disabled={!canUpdate}
                            onClick={() => UpdateRole(row)}
                          >
                            登録
                          </button>
                          <button 
                            style={{ backgroundColor: '#ff4444' }}
                            onClick={() => DeleteRole(row)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementPage;
